import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle, DollarSign, TrendingUp, Download, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CrystalBallResultsProps {
  onSchedule: () => void;
  onDownload: () => void;
}

const predictions = [
  { date: "Tue, Feb 11", time: "2:47 PM", line: "Line 3", type: "Temperature Variance", confidence: 94, impact: 4200, severity: "high", prevention: "Recalibrate temperature sensor on Unit 7B before 1:00 PM" },
  { date: "Tue, Feb 11", time: "4:15 PM", line: "Line 2", type: "Material Thickness", confidence: 89, impact: 2800, severity: "high", prevention: "Adjust feed rate by 3.2% and verify material batch #4471" },
  { date: "Wed, Feb 12", time: "9:23 AM", line: "Line 1", type: "Pressure Drop", confidence: 78, impact: 3900, severity: "medium", prevention: "Schedule filter cleaning during morning break" },
  { date: "Wed, Feb 12", time: "1:30 PM", line: "Line 3", type: "Vibration Anomaly", confidence: 85, impact: 5100, severity: "high", prevention: "Replace bearing assembly #12 — 2hr maintenance window" },
  { date: "Thu, Feb 13", time: "10:45 AM", line: "Line 2", type: "Humidity Drift", confidence: 72, impact: 1900, severity: "medium", prevention: "Activate backup dehumidifier in Zone B" },
  { date: "Thu, Feb 13", time: "3:00 PM", line: "Line 1", type: "Alignment Shift", confidence: 91, impact: 6200, severity: "high", prevention: "Run auto-calibration sequence before afternoon shift" },
  { date: "Fri, Feb 14", time: "8:15 AM", line: "Line 3", type: "Coolant Level", confidence: 67, impact: 2100, severity: "low", prevention: "Top off coolant reservoir — scheduled check" },
  { date: "Fri, Feb 14", time: "11:30 AM", line: "Line 2", type: "Motor Current Spike", confidence: 88, impact: 4800, severity: "high", prevention: "Inspect motor controller firmware for Line 2" },
];

function AnimatedCounter({ target, prefix = "", suffix = "", duration = 2000 }: { target: number; prefix?: string; suffix?: string; duration?: number }) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    let start: number;
    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration]);

  return (
    <span ref={ref}>
      {prefix}{value.toLocaleString()}{suffix}
    </span>
  );
}

export default function CrystalBallResults({ onSchedule, onDownload }: CrystalBallResultsProps) {
  return (
    <section className="relative py-12 px-6 animate-fade-in">
      <div className="max-w-6xl mx-auto">
        {/* Step Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-crystal-success/20 text-crystal-success mb-4">
            <span className="text-xl font-bold">3</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            Prediction Results
          </h2>
          <p className="text-medium-gray">7-day defect forecast based on your production data</p>
        </div>

        {/* Top Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <div className="rounded-2xl border border-crystal-danger/20 bg-crystal-card p-6 text-center">
            <AlertTriangle className="w-8 h-8 text-crystal-danger mx-auto mb-3" />
            <div className="text-4xl md:text-5xl font-bold text-foreground mb-1">
              <AnimatedCounter target={23} />
            </div>
            <p className="text-medium-gray text-sm">Predicted Defects</p>
            <p className="text-xs text-medium-gray mt-1">Next 7 days</p>
          </div>

          <div className="rounded-2xl border border-crystal-success/20 bg-crystal-card p-6 text-center">
            <CheckCircle className="w-8 h-8 text-crystal-success mx-auto mb-3" />
            <div className="text-4xl md:text-5xl font-bold text-crystal-success mb-1">
              <AnimatedCounter target={18} /> <span className="text-xl text-medium-gray">(78%)</span>
            </div>
            <p className="text-medium-gray text-sm">Preventable</p>
            <p className="text-xs text-medium-gray mt-1">With AI recommendations</p>
          </div>

          <div className="rounded-2xl border border-crystal-purple/20 bg-crystal-card p-6 text-center">
            <DollarSign className="w-8 h-8 text-crystal-purple mx-auto mb-3" />
            <div className="text-4xl md:text-5xl font-bold text-crystal-purple mb-1">
              <AnimatedCounter target={47300} prefix="$" />
            </div>
            <p className="text-medium-gray text-sm">Weekly Savings</p>
            <p className="text-xs text-medium-gray mt-1">Potential cost avoidance</p>
          </div>
        </div>

        {/* Predictions Table */}
        <div className="rounded-2xl border border-crystal-purple/15 bg-crystal-card overflow-hidden mb-10">
          <div className="p-6 border-b border-crystal-purple/10">
            <h3 className="text-xl font-bold text-foreground">Detailed Predictions</h3>
            <p className="text-sm text-medium-gray mt-1">Actionable defect forecasts with prevention steps</p>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-crystal-purple/10 text-left">
                  <th className="px-6 py-3 text-medium-gray font-medium">When</th>
                  <th className="px-6 py-3 text-medium-gray font-medium">Line</th>
                  <th className="px-6 py-3 text-medium-gray font-medium">Defect Type</th>
                  <th className="px-6 py-3 text-medium-gray font-medium">Confidence</th>
                  <th className="px-6 py-3 text-medium-gray font-medium">Impact</th>
                  <th className="px-6 py-3 text-medium-gray font-medium">Prevention Action</th>
                </tr>
              </thead>
              <tbody>
                {predictions.map((p, i) => (
                  <tr key={i} className="border-b border-crystal-purple/5 hover:bg-crystal-card-hover transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-foreground font-medium">{p.date}</div>
                      <div className="text-medium-gray text-xs">{p.time}</div>
                    </td>
                    <td className="px-6 py-4 text-foreground">{p.line}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        p.severity === "high"
                          ? "bg-crystal-danger/15 text-crystal-danger"
                          : p.severity === "medium"
                          ? "bg-crystal-warning/15 text-crystal-warning"
                          : "bg-crystal-blue/15 text-crystal-blue"
                      }`}>
                        {p.type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-crystal-purple to-crystal-blue"
                            style={{ width: `${p.confidence}%` }}
                          />
                        </div>
                        <span className="text-foreground text-xs font-medium">{p.confidence}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-crystal-danger font-semibold">${p.impact.toLocaleString()}</td>
                    <td className="px-6 py-4 text-light-gray/70 text-xs max-w-[220px]">{p.prevention}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-crystal-purple/10">
            {predictions.map((p, i) => (
              <div key={i} className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-foreground font-medium text-sm">{p.date} • {p.time}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    p.severity === "high"
                      ? "bg-crystal-danger/15 text-crystal-danger"
                      : p.severity === "medium"
                      ? "bg-crystal-warning/15 text-crystal-warning"
                      : "bg-crystal-blue/15 text-crystal-blue"
                  }`}>
                    {p.severity}
                  </span>
                </div>
                <div className="text-foreground text-sm">{p.line} — {p.type}</div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-medium-gray">Confidence: <strong className="text-foreground">{p.confidence}%</strong></span>
                  <span className="text-crystal-danger font-semibold">${p.impact.toLocaleString()}</span>
                </div>
                <p className="text-light-gray/60 text-xs">{p.prevention}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            onClick={onDownload}
            variant="outline"
            className="border-crystal-purple/30 text-crystal-purple hover:bg-crystal-purple/10 px-6"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Full Report
          </Button>
          <Button
            onClick={onSchedule}
            className="bg-gradient-to-r from-crystal-purple to-crystal-blue hover:opacity-90 text-foreground font-semibold px-8"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Schedule Implementation Call
          </Button>
        </div>
      </div>
    </section>
  );
}
